import React from 'react'
import styled from 'particles/themes/styled'
import { useSelector, useDispatch } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { useToggleCampaignEditorDrawer } from 'hooks/comment'
import qs from 'query-string'
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom'
import { theme } from 'theme'
import { selectSectionErrors } from 'state/error'
import { setConfirmPopup } from 'state/confirmPopup'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import Tab, { TabOption } from 'particles/Tab'
import IconButton from 'particles/Button/IconButton'
import Tooltip from 'particles/Tooltip'
import { TaskSections } from './TaskEditor'
import { useShowCampaignPaymentTab } from 'hooks/permissions'
import { selectEditingCampaign } from 'state/campaigns'

const NavbarWrapper = styled.div`
    width: 100%;
    margin: 0 auto ${theme.gutter.s};
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    #show-comment-center-button {
        margin: 0 24px;
        @media screen and (max-width: 768px) {
            margin: 0;
        }
    }
`

const errorSection = {
    registration: 'setUpErrors',
    tasks: 'tasksErrors',
    publish: 'completeErrors',
    payment: 'paymentErrors',
}

const NavBar: React.FC<{ className?: string }> = ({ className }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const match = useRouteMatch()
    const updateToggleCampaignEditorDrawer = useToggleCampaignEditorDrawer()
    const location = useLocation()
    const history = useHistory()
    const errors = useSelector(selectSectionErrors)
    const params = qs.parse(location.search)
    const prefix = match && match.path
    const campaign = useSelector(selectEditingCampaign)
    const canViewPaymentTab = useShowCampaignPaymentTab(campaign?.has_budget, campaign?.is_private)

    let suffix = ''
    if (params.campaign) {
        suffix = '?campaign=' + params.campaign
    }
    if (params?.drawer) {
        suffix = suffix + `&drawer=${params?.drawer}`
    }
    const navOnClick = (direction: string, to: string) => {
        const isCampaignEditorValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        if (isCampaignEditorValueChange && location.pathname !== to) {
            dispatch(
                setConfirmPopup({
                    show: true,
                    onConfirm: () => {
                        history.push(direction)
                    },
                }),
            )
        } else {
            history.push(direction)
        }
    }

    const tabOptions: TabOption[] = [
        {
            id: `${prefix}/registration`,
            label: t('breadcrumb.campaignEditor.setUpCampaign'),
            disabled: false,
            isError: errors[errorSection.registration],
            onClick: () => navOnClick(`${prefix}/registration${suffix}`, `${prefix}/registration`),
        },
        {
            id: `${prefix}/tasks`,
            label: t('breadcrumb.campaignEditor.createTasks'),
            disabled: Boolean(!params.campaign),
            isError: errors[errorSection.tasks],
            onClick: () => navOnClick(`${prefix}/tasks${suffix}&section=${TaskSections.SETUP}`, `${prefix}/tasks`),
        },
        {
            id: `${prefix}/publish`,
            label: t('breadcrumb.campaignEditor.completeSetup'),
            disabled: Boolean(!params.campaign),
            isError: errors[errorSection.publish],
            onClick: () => navOnClick(`${prefix}/publish${suffix}`, `${prefix}/publish`),
        },
        {
            id: `${prefix}/payment`,
            label: t('breadcrumb.campaignEditor.campaignPayment'),
            disabled: Boolean(!params.campaign),
            isError: errors[errorSection.payment],
            onClick: () => navOnClick(`${prefix}/payment${suffix}`, `${prefix}/payment`),
            isHidden: !canViewPaymentTab,
        },
    ]
    return (
        <div className={className}>
            <NavbarWrapper>
                <Tab
                    dataTestId="test-campaign-tab"
                    tabItemAlign="left"
                    tabs={tabOptions}
                    selectedTab={location.pathname}
                    form="steps"
                />
                <Tooltip label={t('campaignEditor.comment.viewAllCommentsTooltipTitle')} placement="left">
                    <IconButton
                        icon="chatText"
                        form="outlined"
                        size="s"
                        color="neutral"
                        id="show-comment-center-button"
                        onClick={() => updateToggleCampaignEditorDrawer('comments')}
                        isActivated={params?.drawer === 'comments'}
                    />
                </Tooltip>
            </NavbarWrapper>
        </div>
    )
}

export default NavBar
